import { format } from "date-fns";
import { fetchCookie } from "./cookies";

/**
 * Reads API error payloads in either shape:
 * - { errors: [{ message, field? }] }
 * - { error: "..." }
 */
export const getApiErrorMessage = (
  err: any,
  fallback = "Something went wrong",
): string => {
  if (!err) return fallback;
  if (typeof err === "string" && err.trim()) return err;

  if (typeof err?.error === "string" && err.error.trim()) {
    return err.error;
  }

  if (Array.isArray(err?.errors) && err.errors.length > 0) {
    const messages = err.errors
      .map((e: any) => (typeof e === "string" ? e : e?.message))
      .filter(
        (m: unknown): m is string => typeof m === "string" && m.trim().length > 0,
      );
    if (messages.length) return messages.join(", ");
  }

  if (typeof err?.message === "string" && err.message.trim()) {
    return err.message;
  }

  return fallback;
};

export const isEmpty = (object: object | null): boolean =>
  object === null ||
  Object.keys(object).length <= 0 ||
  Object.values(object).every((x) => x === null || x === "");

export function separateFullName(fullName: string) {
  const nameParts = fullName.trim().split(" ");
  const firstName = nameParts[0];
  const lastName = nameParts.slice(1).join(" ");

  return {
    firstName,
    lastName,
  };
}

export const addPageToSessionStorage = (page: number) => {
  sessionStorage.setItem("propertyListPage", JSON.stringify(page));
};
export const getPageFromSessionStorage = () => {
  let pageString = sessionStorage.getItem("propertyListPage");
  if (pageString) {
    JSON.parse(pageString);
  } else {
    pageString = "1";
  }
  return pageString;
};

export const phoneNumberCheck =
  /(\+?\d{1,4}[\s.-]?[a-zA-Z0-9]{1,3}[\s.-]?[a-zA-Z0-9]{1,4}[\s.-]?[a-zA-Z0-9]{1,4}[\s.-]?[a-zA-Z0-9]{1,9}|(?:zero|one|two|three|four|five|six|seven|eight|nine)[\s.-]*)+/g;

export const phoneNumberRequestCheck =
  /\b(?:call\s+me|phone\s*number|mobile\s*number|(?:can|may|could|would|please|might|should)\s*(?:you\s+)?(?:give|send|provide|share|tell|text|show)\s*(?:me\s+)?(?:your\s+|a\s+)?phone\s*number|number|digits|contact\s*number|cell\s*number|mobile\s*number)\b/gi;

export function numberWithCommas(x: string) {
  return x?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export const toCalendarDateString = (date: Date | string): string => {
  const d = typeof date === "string" ? new Date(date) : date;
  return format(d, "yyyy-MM-dd");
};

export const parseCalendarDate = (dateStr: string): Date => {
  const [year, month, day] = dateStr.split("T")[0].split("-").map(Number);
  return new Date(year, month - 1, day, 12, 0, 0);
};

export const formatDate = (date: Date | string) => {
  return toCalendarDateString(date);
};

export const formatDateRange = (range: any) => {
  const formatToDateString = (date: Date) => {
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };
  return `${formatToDateString(range.start)} - ${formatToDateString(
    range.end
  )}`;
};
export const formatDateRangeIncrement = (range: any) => {
  const formatToDateString = (date: Date) => {
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() + 1); // Add 1 day
    const day = newDate.getDate().toString().padStart(2, "0");
    const month = (newDate.getMonth() + 1).toString().padStart(2, "0");
    const year = newDate.getFullYear();
    return `${day}/${month}/${year}`;
  };

  return `${formatToDateString(range.start)} - ${formatToDateString(
    range.end
  )}`;
};

export const formatCurrency = (value: any) => {
  if (value === undefined || value === null || isNaN(Number(value))) {
    return "0.00";
  }

  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const isAuthenticated = () => {
  return fetchCookie("1Q_SPA") ? true : false;
};

export const formatDateToTime = (isoDate: string): string => {
  const date = new Date(isoDate);
  const hours = date.getHours().toString().padStart(2, "0"); // Get hours and add leading zero if necessary
  const minutes = date.getMinutes().toString().padStart(2, "0"); // Get minutes and add leading zero if necessary
  return `${hours}:${minutes}`;
};
export const isValidDate = (date: Date | null, inspectionSchedules: any) => {
  if (!date) return false;
  if (!inspectionSchedules?.option1?.from || !inspectionSchedules?.option2?.to)
    return false;

  const option1Date = new Date(inspectionSchedules.option1.from);
  const option2Date = new Date(inspectionSchedules.option2.to);

  const normalizedDate = new Date(date);
  normalizedDate.setHours(0, 0, 0, 0);

  option1Date.setHours(0, 0, 0, 0);
  option2Date.setHours(0, 0, 0, 0);

  return (
    normalizedDate.getTime() === option1Date.getTime() ||
    normalizedDate.getTime() === option2Date.getTime()
  );
};
export const formatNumberWithCommas = (value: string | number): any => {
  const cleanValue = value?.toString().replace(/[^\d.]/g, "");

  const number = parseFloat(cleanValue);

  if (isNaN(number)) return "";

  return number.toLocaleString("en-US");
};
export const newFormatNumberWithCommas = (value: string) => {
  const parts = value.split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts.join(".");
};
export const formatPrice = (price: number): string => {
  if (price >= 1_000_000) {
    const formatted = (price / 1_000_000).toFixed(
      price % 1_000_000 === 0 ? 0 : 1
    );
    return `₦${formatted}m`;
  }
  if (price >= 1_000) {
    const formatted = (price / 1_000).toFixed(price % 1_000 === 0 ? 0 : 1);
    return `₦${formatted}k`;
  }

  return `${formatCurrency(price)}`;
};

export function formatDateTime(
  isoDate: string,
  options?: Intl.DateTimeFormatOptions
) {
  if (!isoDate) return "";

  const date = new Date(isoDate);

  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };

  return date.toLocaleString("en-US", options || defaultOptions);
}
export const formatTime = (value: number) => {
  const minutes = Math.floor(value / 60);
  const seconds = value % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

export const getSearchResultListings = (result: any) => {
  if (Array.isArray(result?.data) && result.data.length > 0) return result.data;
  if (Array.isArray(result?.suggestions) && result.suggestions.length > 0) {
    return result.suggestions;
  }
  return [];
};

export const toExploreSearchState = (res: any) => ({
  properties: Array.isArray(res?.data) ? res.data : [],
  suggestions: Array.isArray(res?.suggestions) ? res.suggestions : [],
  page_count: res?.page_count,
  last_page: res?.last_page,
  total: res?.total,
  current_page: res?.current_page,
  numOfPages: res?.last_page || 1,
  suggestionLabel: res?.suggestionLabel,
  match: res?.match,
});
